import type {
  FeaturedBadgeColorSlug,
  FeaturedBadgeColorSwatch,
  FeaturedBadgeColorVariant,
} from "../../../models/featuredBadgeColorPalette";
import { FEATURED_BADGE_COLOR_SLUGS } from "../../../models/featuredBadgeColorPalette";

export const FEATURED_BADGE_COLOR_VARIANT_OPTIONS: FeaturedBadgeColorVariant[] = [1, 2, 3];

// Fallback swatches used when the tenant theme has no featuredEventBadges object.
// Variant 1 matches frontend-app develop defaults; variants 2/3 are the same color
// slightly lighter (closed is near-white, so its variants go a touch darker).
const FALLBACK_SWATCHES: Record<
  FeaturedBadgeColorSlug,
  Record<FeaturedBadgeColorVariant, FeaturedBadgeColorSwatch>
> = {
  closed: {
    1: { backgroundColor: "#F5F5F5", textColor: "#989898" },
    2: { backgroundColor: "#EBEBEB", textColor: "#989898" },
    3: { backgroundColor: "#E0E0E0", textColor: "#989898" },
  },
  danger: {
    1: { backgroundColor: "#D32F2F", textColor: "#FFFFFF" },
    2: { backgroundColor: "#D84848", textColor: "#FFFFFF" },
    3: { backgroundColor: "#DE6161", textColor: "#FFFFFF" },
  },
  urgent: {
    1: { backgroundColor: "#D32F2F", textColor: "#FFFFFF" },
    2: { backgroundColor: "#D84848", textColor: "#FFFFFF" },
    3: { backgroundColor: "#DE6161", textColor: "#FFFFFF" },
  },
  soon: {
    1: { backgroundColor: "#FF9800", textColor: "#FFFFFF" },
    2: { backgroundColor: "#FFA41F", textColor: "#FFFFFF" },
    3: { backgroundColor: "#FFB13D", textColor: "#FFFFFF" },
  },
  warning: {
    1: { backgroundColor: "#FF9800", textColor: "#FFFFFF" },
    2: { backgroundColor: "#FFA41F", textColor: "#FFFFFF" },
    3: { backgroundColor: "#FFB13D", textColor: "#FFFFFF" },
  },
  notice: {
    1: { backgroundColor: "#63C6BD", textColor: "#FFFFFF" },
    2: { backgroundColor: "#76CDC5", textColor: "#FFFFFF" },
    3: { backgroundColor: "#88D4CD", textColor: "#FFFFFF" },
  },
};

const parseTenantTheme = (theme: string | undefined): Record<string, unknown> | null => {
  if (!theme) return null;
  try {
    const parsed = JSON.parse(theme);
    return typeof parsed === "object" && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
};

// Reads the explicit per-variant colors from the tenant theme
// (featuredEventBadges[slug]["1"|"2"|"3"]). Falls back to the develop defaults
// per variant when the tenant theme does not define a color for that state.
const resolveSwatchesForSlug = (
  slug: FeaturedBadgeColorSlug,
  tenantTheme: Record<string, unknown> | null
): Record<FeaturedBadgeColorVariant, FeaturedBadgeColorSwatch> => {
  const palette = tenantTheme?.featuredEventBadges as
    | Record<string, Record<string, { bg?: string; text?: string }>>
    | undefined;
  const entry = palette?.[slug];

  const result = {} as Record<FeaturedBadgeColorVariant, FeaturedBadgeColorSwatch>;
  for (const variant of FEATURED_BADGE_COLOR_VARIANT_OPTIONS) {
    const fromTheme = entry?.[String(variant)];
    result[variant] = fromTheme?.bg
      ? { backgroundColor: fromTheme.bg, textColor: fromTheme.text ?? "#FFFFFF" }
      : FALLBACK_SWATCHES[slug][variant];
  }
  return result;
};

export const getFeaturedBadgeSwatchesForTenant = (
  tenantTheme: string | undefined
): Record<FeaturedBadgeColorSlug, Record<FeaturedBadgeColorVariant, FeaturedBadgeColorSwatch>> => {
  const parsed = parseTenantTheme(tenantTheme);
  const result = {} as Record<
    FeaturedBadgeColorSlug,
    Record<FeaturedBadgeColorVariant, FeaturedBadgeColorSwatch>
  >;
  for (const slug of FEATURED_BADGE_COLOR_SLUGS) {
    result[slug] = resolveSwatchesForSlug(slug, parsed);
  }
  return result;
};

export const getFeaturedBadgeSwatch = (
  swatches: Record<FeaturedBadgeColorSlug, Record<FeaturedBadgeColorVariant, FeaturedBadgeColorSwatch>>,
  slug: FeaturedBadgeColorSlug,
  variant: FeaturedBadgeColorVariant
): FeaturedBadgeColorSwatch => swatches[slug][variant];
